import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Rate,
  Tag,
  Select,
  Input,
  Tabs,
  Button,
  Avatar,
  Space,
  Empty,
  Spin,
  message,
} from 'antd';
import {
  StarOutlined,
  UserOutlined,
  BookOutlined,
  CommentOutlined,
  SearchOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  LikeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAllCourseFeedbacks } from '../../../Constants/Api/functions/function';
import './styles.scss';

const { Option } = Select;
const { TabPane } = Tabs;

const Feedbacks = () => {
  const userProfile = useSelector((state) => state.userProfile?.userProfile);
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState({
    totalReviews: 0,
    avgCourseRating: 5.0,
    avgLecturerRating: 5.0,
    avgClarityRating: 5.0,
  });
  const [loading, setLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [selectedLecturer, setSelectedLecturer] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (userProfile?.role === 'instructor') {
        params.role = 'instructor';
        params.userId = userProfile.id;
      }
      const res = await getAllCourseFeedbacks(params);
      if (res && res.success) {
        setFeedbacks(res.feedbacks || []);
        if (res.stats) {
          setStats(res.stats);
        }
      }
    } catch (err) {
      console.error('Fetch feedback error:', err);
      message.error('Failed to load student feedbacks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [userProfile]);

  // Unique courses & lecturers for filters
  const uniqueCourses = useMemo(() => {
    const map = new Map();
    feedbacks.forEach((f) => {
      if (f.courseId && !map.has(f.courseId)) {
        map.set(f.courseId, f.courseName || `Course #${f.courseCode || f.courseId}`);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [feedbacks]);

  const uniqueLecturers = useMemo(() => {
    const map = new Map();
    feedbacks.forEach((f) => {
      if (f.lecturerId && !map.has(f.lecturerId)) {
        map.set(f.lecturerId, f.lecturerName || 'Assigned Lecturer');
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [feedbacks]);

  // Filtered feedbacks
  const filteredFeedbacks = useMemo(() => {
    return feedbacks.filter((item) => {
      if (selectedCourse !== 'all' && item.courseId !== selectedCourse) return false;
      if (selectedLecturer !== 'all' && item.lecturerId !== selectedLecturer) return false;
      if (ratingFilter !== 'all') {
        const r = Number(ratingFilter);
        if (Math.round(item.ratingCourse) !== r && Math.round(item.ratingLecturer) !== r) return false;
      }
      if (searchText.trim()) {
        const s = searchText.toLowerCase();
        const matchCourse = (item.courseName || '').toLowerCase().includes(s);
        const matchLecturer = (item.lecturerName || '').toLowerCase().includes(s);
        const matchStudent = (item.studentName || '').toLowerCase().includes(s);
        const matchComment = (item.comments || '').toLowerCase().includes(s);
        if (!matchCourse && !matchLecturer && !matchStudent && !matchComment) return false;
      }
      return true;
    });
  }, [feedbacks, selectedCourse, selectedLecturer, ratingFilter, searchText]);

  // Grouped by course
  const courseWiseData = useMemo(() => {
    const groups = {};
    filteredFeedbacks.forEach((f) => {
      const cId = f.courseId || 'unknown';
      if (!groups[cId]) {
        groups[cId] = {
          courseId: cId,
          courseName: f.courseName || 'Course',
          courseCode: f.courseCode,
          lecturerName: f.lecturerName,
          reviews: [],
        };
      }
      groups[cId].reviews.push(f);
    });

    return Object.values(groups).map((g) => {
      const total = g.reviews.length;
      const avgCourse = (g.reviews.reduce((s, r) => s + Number(r.ratingCourse || 0), 0) / total).toFixed(1);
      const avgLecturer = (g.reviews.reduce((s, r) => s + Number(r.ratingLecturer || 0), 0) / total).toFixed(1);
      return {
        ...g,
        totalReviews: total,
        avgCourseRating: parseFloat(avgCourse),
        avgLecturerRating: parseFloat(avgLecturer),
      };
    });
  }, [filteredFeedbacks]);

  // Grouped by lecturer
  const lecturerWiseData = useMemo(() => {
    const groups = {};
    filteredFeedbacks.forEach((f) => {
      const lId = f.lecturerId || 'unassigned';
      if (!groups[lId]) {
        groups[lId] = {
          lecturerId: lId,
          lecturerName: f.lecturerName || 'Lecturer / Instructor',
          lecturerProfileImage: f.lecturerProfileImage,
          coursesTaught: new Set(),
          reviews: [],
        };
      }
      if (f.courseName) groups[lId].coursesTaught.add(f.courseName);
      groups[lId].reviews.push(f);
    });

    return Object.values(groups).map((g) => {
      const total = g.reviews.length;
      const avgLecturer = (g.reviews.reduce((s, r) => s + Number(r.ratingLecturer || 0), 0) / total).toFixed(1);
      const avgCourse = (g.reviews.reduce((s, r) => s + Number(r.ratingCourse || 0), 0) / total).toFixed(1);
      return {
        ...g,
        coursesTaught: Array.from(g.coursesTaught),
        totalReviews: total,
        avgLecturerRating: parseFloat(avgLecturer),
        avgCourseRating: parseFloat(avgCourse),
      };
    });
  }, [filteredFeedbacks]);

  // Export to Excel
  const handleExport = () => {
    if (filteredFeedbacks.length === 0) {
      message.warning('No feedback data available to export');
      return;
    }

    const rows = filteredFeedbacks.map((f) => ({
      'Feedback ID': f.id,
      'Course Name': f.courseName || 'N/A',
      'Course Code': f.courseCode || 'N/A',
      'Lecturer Name': f.lecturerName || 'N/A',
      'Student Name': f.studentName || 'Anonymous',
      'Student Email': f.studentEmail || 'Confidential',
      'Student Number': f.studentNumber || 'N/A',
      'Course Rating': f.ratingCourse,
      'Lecturer Rating': f.ratingLecturer,
      'Content Clarity': f.contentClarity,
      'Comments': f.comments || '',
      'Is Anonymous': f.isAnonymous ? 'Yes' : 'No',
      'Submitted Date': f.createdAt ? dayjs(f.createdAt).format('YYYY-MM-DD HH:mm') : 'N/A',
    }));

    Promise.all([import('xlsx'), import('file-saver')])
      .then(([xlsx, FileSaver]) => {
        const wb = xlsx.utils.book_new();
        const ws = xlsx.utils.json_to_sheet(rows);
        xlsx.utils.book_append_sheet(wb, ws, 'Feedback Submissions');
        const excelBuffer = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
        });
        FileSaver.default.saveAs(blob, `Course_Lecturer_Feedbacks_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
        message.success('Feedback report exported successfully!');
      })
      .catch((err) => {
        console.error('Export error:', err);
        message.error('Failed to export feedback report');
      });
  };

  // Shared columns for feedback table
  const columns = [
    {
      title: 'Course',
      dataIndex: 'courseName',
      key: 'courseName',
      render: (text, record) => (
        <div>
          <strong style={{ color: '#03245e' }}>{text}</strong>
          {record.courseCode && (
            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Code: {record.courseCode}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Lecturer',
      dataIndex: 'lecturerName',
      key: 'lecturerName',
      render: (name) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} style={{ background: '#1890ff' }} />
          <span>{name || 'Assigned Instructor'}</span>
        </Space>
      ),
    },
    {
      title: 'Course Rating',
      dataIndex: 'ratingCourse',
      key: 'ratingCourse',
      align: 'center',
      sorter: (a, b) => a.ratingCourse - b.ratingCourse,
      render: (val) => (
        <div>
          <Rate disabled allowHalf value={Number(val)} style={{ fontSize: 13, color: '#f59e0b' }} />
          <span style={{ marginLeft: 6, fontWeight: 600, color: '#0f172a' }}>{Number(val).toFixed(1)}</span>
        </div>
      ),
    },
    {
      title: 'Lecturer Rating',
      dataIndex: 'ratingLecturer',
      key: 'ratingLecturer',
      align: 'center',
      sorter: (a, b) => a.ratingLecturer - b.ratingLecturer,
      render: (val) => (
        <div>
          <Rate disabled allowHalf value={Number(val)} style={{ fontSize: 13, color: '#10b981' }} />
          <span style={{ marginLeft: 6, fontWeight: 600, color: '#0f172a' }}>{Number(val).toFixed(1)}</span>
        </div>
      ),
    },
    {
      title: 'Student Comments',
      key: 'comments',
      width: '30%',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>
            {record.isAnonymous ? (
              <Tag color="default">Anonymous Student</Tag>
            ) : (
              <span>{record.studentName} {record.studentNumber ? `(${record.studentNumber})` : ''}</span>
            )}
          </div>
          {record.comments ? (
            <div className="comment-quote">“{record.comments}”</div>
          ) : (
            <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>No additional written comments</span>
          )}
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d) => (
        <span style={{ fontSize: '12px', color: '#64748b' }}>
          {d ? dayjs(d).format('YYYY-MM-DD HH:mm') : 'N/A'}
        </span>
      ),
    },
  ];

  return (
    <div className="feedbacks-page">
      <div className="feedbacks-header">
        <div>
          <h2>Course & Lecturer Feedback Analytics</h2>
          <p>Review student feedback, ratings, and suggestions analyzed lecturer-wise and course-wise.</p>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExport}
          className="export-btn"
        >
          Export Feedback Report
        </Button>
      </div>

      {/* STAT CARDS */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="glass-card stat-card-premium">
            <Statistic
              title="Total Feedback Submissions"
              value={stats.totalReviews}
              prefix={<CommentOutlined style={{ color: '#1890ff', marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="glass-card stat-card-premium">
            <Statistic
              title="Course Content Rating"
              value={stats.avgCourseRating}
              suffix="/ 5.0"
              precision={1}
              prefix={<StarOutlined style={{ color: '#f59e0b', marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="glass-card stat-card-premium">
            <Statistic
              title="Lecturer Quality Rating"
              value={stats.avgLecturerRating}
              suffix="/ 5.0"
              precision={1}
              prefix={<LikeOutlined style={{ color: '#10b981', marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="glass-card stat-card-premium">
            <Statistic
              title="Content Clarity Score"
              value={stats.avgClarityRating}
              suffix="/ 5.0"
              precision={1}
              prefix={<CheckCircleOutlined style={{ color: '#8b5cf6', marginRight: 8 }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* FILTER BAR */}
      <Card className="glass-card">
        <div className="filter-bar">
          <Input
            placeholder="Search by course, lecturer, student, or keyword..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />

          <Select
            value={selectedCourse}
            onChange={(val) => setSelectedCourse(val)}
            style={{ width: 240 }}
          >
            <Option value="all">All Courses</Option>
            {uniqueCourses.map((c) => (
              <Option key={c.id} value={c.id}>{c.name}</Option>
            ))}
          </Select>

          <Select
            value={selectedLecturer}
            onChange={(val) => setSelectedLecturer(val)}
            style={{ width: 220 }}
          >
            <Option value="all">All Lecturers</Option>
            {uniqueLecturers.map((l) => (
              <Option key={l.id} value={l.id}>{l.name}</Option>
            ))}
          </Select>

          <Select
            value={ratingFilter}
            onChange={(val) => setRatingFilter(val)}
            style={{ width: 160 }}
          >
            <Option value="all">All Star Ratings</Option>
            <Option value="5">⭐⭐⭐⭐⭐ (5 Stars)</Option>
            <Option value="4">⭐⭐⭐⭐ (4 Stars)</Option>
            <Option value="3">⭐⭐⭐ (3 Stars)</Option>
            <Option value="2">⭐⭐ (2 Stars)</Option>
            <Option value="1">⭐ (1 Star)</Option>
          </Select>
        </div>
      </Card>

      {/* TABS: Course-wise, Lecturer-wise, All Feedbacks */}
      <Card className="glass-card">
        <Spin spinning={loading}>
          <Tabs defaultActiveKey="course-wise" type="card">
            {/* TAB 1: COURSE-WISE */}
            <TabPane
              tab={
                <span>
                  <BookOutlined /> Course-wise Feedback ({courseWiseData.length})
                </span>
              }
              key="course-wise"
            >
              {courseWiseData.length === 0 ? (
                <Empty description="No feedback records found matching filters" />
              ) : (
                courseWiseData.map((courseItem) => (
                  <div key={courseItem.courseId} className="feedback-course-card">
                    <div className="card-top">
                      <div>
                        <span className="title">{courseItem.courseName}</span>
                        {courseItem.lecturerName && (
                          <span style={{ marginLeft: 12, fontSize: 13, color: '#64748b' }}>
                            • Lecturer: <strong>{courseItem.lecturerName}</strong>
                          </span>
                        )}
                      </div>
                      <div className="badge-ratings">
                        <Tag color="blue" style={{ fontSize: 13, padding: '3px 10px' }}>
                          Total Reviews: {courseItem.totalReviews}
                        </Tag>
                        <Tag color="gold" style={{ fontSize: 13, padding: '3px 10px' }}>
                          Course: ★ {courseItem.avgCourseRating} / 5.0
                        </Tag>
                        <Tag color="green" style={{ fontSize: 13, padding: '3px 10px' }}>
                          Lecturer: ★ {courseItem.avgLecturerRating} / 5.0
                        </Tag>
                      </div>
                    </div>
                    <Table
                      dataSource={courseItem.reviews}
                      columns={columns.filter((c) => c.key !== 'courseName')}
                      rowKey="id"
                      pagination={{ pageSize: 5 }}
                      size="middle"
                    />
                  </div>
                ))
              )}
            </TabPane>

            {/* TAB 2: LECTURER-WISE */}
            <TabPane
              tab={
                <span>
                  <UserOutlined /> Lecturer-wise Feedback ({lecturerWiseData.length})
                </span>
              }
              key="lecturer-wise"
            >
              {lecturerWiseData.length === 0 ? (
                <Empty description="No lecturer feedback records found matching filters" />
              ) : (
                lecturerWiseData.map((lecItem) => (
                  <div key={lecItem.lecturerId} className="feedback-lecturer-card">
                    <div className="card-top">
                      <Space size="middle">
                        <Avatar
                          size={40}
                          src={lecItem.lecturerProfileImage}
                          icon={<UserOutlined />}
                          style={{ background: '#1890ff' }}
                        />
                        <div>
                          <div className="title">{lecItem.lecturerName}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            Courses: {lecItem.coursesTaught.join(', ') || 'N/A'}
                          </div>
                        </div>
                      </Space>
                      <div className="badge-ratings">
                        <Tag color="blue" style={{ fontSize: 13, padding: '3px 10px' }}>
                          Total Student Reviews: {lecItem.totalReviews}
                        </Tag>
                        <Tag color="green" style={{ fontSize: 13, padding: '3px 10px' }}>
                          Lecturer Rating: ★ {lecItem.avgLecturerRating} / 5.0
                        </Tag>
                        <Tag color="gold" style={{ fontSize: 13, padding: '3px 10px' }}>
                          Course Quality: ★ {lecItem.avgCourseRating} / 5.0
                        </Tag>
                      </div>
                    </div>
                    <Table
                      dataSource={lecItem.reviews}
                      columns={columns.filter((c) => c.key !== 'lecturerName')}
                      rowKey="id"
                      pagination={{ pageSize: 5 }}
                      size="middle"
                    />
                  </div>
                ))
              )}
            </TabPane>

            {/* TAB 3: ALL FEEDBACKS */}
            <TabPane
              tab={
                <span>
                  <CommentOutlined /> All Feedback List ({filteredFeedbacks.length})
                </span>
              }
              key="all-feedbacks"
            >
              <Table
                dataSource={filteredFeedbacks}
                columns={columns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="middle"
              />
            </TabPane>
          </Tabs>
        </Spin>
      </Card>
    </div>
  );
};

export default Feedbacks;
